/* 1b — Watercraft homepage banner, mobile, expanded.
   Ported from `01-placements/1b_homepage-banner-mobile-expanded.html`.

   The pack ships four mobile variants (1b–1e) and says to use exactly one.
   This is the always-expanded one, for when the banner is the page's
   headline moment; 1c is the collapsed default for a busier feed.

   The pack's asset is a fixed 390px wide. Here it runs fluid, since it has
   to sit in a real phone viewport. */
import React, { useCallback, useRef, useState } from 'react';
import WatercraftCards, { WATERCRAFT_CARD_COUNT } from './WatercraftCards';
import { wcColors, wcFonts, wcOnAccent, wcSolidTint } from './tokens';
import { ReactComponent as Wordmark } from '../../assets/images/watercraft/wc_wordmark.svg';
import '../../assets/css/watercraft_banner.css';

const WATERCRAFT_URL = 'https://watercraft.app';

// TODO: swap for the real store listings once Watercraft is published, and
// replace the text lockups below with the official Apple/Google artwork.
const APP_STORE_URL = WATERCRAFT_URL;
const GOOGLE_PLAY_URL = WATERCRAFT_URL;

function AppleIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      style={{ width: '13px', height: '16px', display: 'block' }}
      fill={wcColors.textPrimary}
      aria-hidden='true'
    >
      <path d='M17.05 12.53c-.02-2.2 1.79-3.26 1.87-3.31-1.02-1.49-2.61-1.7-3.17-1.72-1.35-.14-2.63.79-3.31.79-.68 0-1.79-.77-2.94-.75-1.51.02-2.9.88-3.68 2.23-1.57 2.72-.4 6.74 1.13 8.95.75 1.08 1.64 2.24 2.81 2.2 1.13-.04 1.56-.73 2.93-.73 1.36 0 1.75.73 2.94.71 1.21-.02 1.98-1.1 2.72-2.19.85-1.25 1.2-2.46 1.22-2.52-.03-.01-2.34-.9-2.36-3.56z' />
      <path d='M14.9 6.02c.6-.72 1-1.72.89-2.72-.86.03-1.9.57-2.52 1.29-.55.63-1.03 1.65-.9 2.62.96.07 1.93-.49 2.53-1.19z' />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      style={{ width: '13px', height: '16px', display: 'block' }}
      aria-hidden='true'
    >
      <path
        d='M4 2.4 L18.6 12 L4 21.6 Z'
        fill='none'
        stroke={wcColors.textPrimary}
        strokeWidth='1.8'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function StoreLink({ href, icon, children }) {
  return (
    <a
      className='wc-1b__store'
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      <span
        style={{
          fontFamily: wcFonts.body,
          fontSize: '12.5px',
          fontWeight: '500',
          color: wcColors.textPrimary,
        }}
      >
        {children}
      </span>
    </a>
  );
}

export default function WatercraftBannerMobile() {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);

  /* The pack renders the dots in a fixed state. Against a live scroller a
     static indicator would be misleading, so they track scroll position —
     nearest card to the centre line, which is where scroll-snap lands. */
  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const centre = el.scrollLeft + el.clientWidth / 2;
    let nearest = 0;
    let shortest = Infinity;
    Array.from(el.children).forEach((card, i) => {
      const distance = Math.abs(
        card.offsetLeft + card.offsetWidth / 2 - centre
      );
      if (distance < shortest) {
        shortest = distance;
        nearest = i;
      }
    });
    setActive(nearest);
  }, []);

  const goToCard = (i) => {
    const el = scrollerRef.current;
    const card = el && el.children[i];
    if (!card) return;
    const left = card.offsetLeft + card.offsetWidth / 2 - el.clientWidth / 2;

    /* Safari below 15.4 ignores ScrollToOptions outright, which would leave
       the dots looking interactive but doing nothing — so jump there
       instead. Reduced motion gets the jump too. */
    const canSmoothScroll =
      'scrollBehavior' in document.documentElement.style &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (canSmoothScroll) {
      el.scrollTo({ left, behavior: 'smooth' });
    } else {
      el.scrollLeft = left;
    }
  };

  return (
    <div className='wc-1b'>
      <div
        style={{
          width: '100%',
          position: 'relative',
          background: wcColors.bg,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <div
          aria-hidden='true'
          style={{
            position: 'absolute',
            left: '-110px',
            top: '-70px',
            width: '380px',
            height: '320px',
            borderRadius: '100%',
            background:
              'radial-gradient(circle,rgba(61,255,106,.34) 0%,rgba(61,255,106,.08) 48%,rgba(61,255,106,0) 72%)',
            filter: 'blur(40px)',
            pointerEvents: 'none',
          }}
        />
        <div
          aria-hidden='true'
          style={{
            position: 'absolute',
            left: '90px',
            bottom: '-130px',
            width: '340px',
            height: '300px',
            borderRadius: '100%',
            background:
              'radial-gradient(circle,rgba(61,255,106,.2) 0%,rgba(26,92,42,.1) 50%,rgba(61,255,106,0) 74%)',
            filter: 'blur(46px)',
            pointerEvents: 'none',
          }}
        />

        <div style={{ position: 'relative', padding: '22px 18px 24px' }}>
          <div
            style={{
              position: 'relative',
              background:
                'linear-gradient(140deg,rgba(61,255,106,.13) 0%,rgba(61,255,106,.05) 42%,rgba(238,244,235,.03) 100%)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              border: '1px solid rgba(61,255,106,.24)',
              borderRadius: '18px',
              padding: '26px 20px 22px',
              boxShadow:
                '0 20px 50px rgba(0,0,0,.35),inset 0 1px 0 rgba(238,244,235,.1)',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: '0',
                right: '22px',
                transform: 'translateY(-50%)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                // Opaque for the same reason as 1a — it straddles the panel edge.
                background: wcSolidTint(wcColors.primary, 0.25),
                border: '1px solid rgba(61,255,106,.6)',
                color: wcColors.accent,
                fontFamily: wcFonts.mono,
                fontSize: '8.5px',
                fontWeight: '600',
                letterSpacing: '.2em',
                textTransform: 'uppercase',
                padding: '5px 11px',
                borderRadius: '100px',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 18px rgba(61,255,106,.22)',
              }}
            >
              <span
                style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '100px',
                  background: wcColors.accent,
                  display: 'block',
                }}
              />
              New
            </span>

            <Wordmark
              role='img'
              aria-label='Watercraft'
              style={{
                width: '66px',
                height: '42px',
                display: 'block',
                marginBottom: '18px',
                color: wcColors.textPrimary,
              }}
            />

            <h2
              style={{
                fontFamily: wcFonts.display,
                fontSize: '31px',
                lineHeight: '1.06',
                fontWeight: '600',
                color: wcColors.textPrimary,
                margin: '0 0 12px',
                letterSpacing: '-.015em',
              }}
            >
              You book the session.
              <span
                style={{
                  display: 'block',
                  fontStyle: 'italic',
                  fontWeight: '400',
                  color: wcColors.textReading,
                }}
              >
                Watercraft remembers the details.
              </span>
            </h2>

            <p
              style={{
                fontFamily: wcFonts.body,
                fontSize: '14px',
                lineHeight: '1.6',
                color: wcColors.textBody,
                fontWeight: '300',
                margin: '0 0 22px',
              }}
            >
              A private field tool for what you work out on the bank. Catches,
              sessions, spots, conditions. Default private, and it gives it all
              back when you ask.
            </p>

            <a
              className='wc-1b__cta'
              href={WATERCRAFT_URL}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                display: 'block',
                textAlign: 'center',
                background: wcColors.accent,
                color: wcOnAccent,
                fontFamily: wcFonts.body,
                fontSize: '15px',
                fontWeight: '700',
                padding: '15px',
                borderRadius: '11px',
                whiteSpace: 'nowrap',
                marginBottom: '16px',
              }}
            >
              Get Watercraft FREE
            </a>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '22px',
                flexWrap: 'wrap',
              }}
            >
              <StoreLink href={APP_STORE_URL} icon={<AppleIcon />}>
                App Store
              </StoreLink>
              <StoreLink href={GOOGLE_PLAY_URL} icon={<PlayIcon />}>
                Google Play
              </StoreLink>
            </div>
          </div>
        </div>

        <div style={{ position: 'relative', padding: '0 0 24px 0' }}>
          <div
            className='wc-1b__scroller'
            ref={scrollerRef}
            onScroll={handleScroll}
            style={{
              display: 'flex',
              gap: '12px',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              padding: '0 18px',
            }}
          >
            <WatercraftCards />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '6px',
              padding: '16px 0 0',
            }}
          >
            {Array.from({ length: WATERCRAFT_CARD_COUNT }).map((_, i) => (
              <button
                key={i}
                type='button'
                className='wc-1b__dot'
                aria-label={`Show card ${i + 1} of ${WATERCRAFT_CARD_COUNT}`}
                aria-current={i === active}
                onClick={() => goToCard(i)}
                style={{
                  width: i === active ? '18px' : '6px',
                  height: '6px',
                  borderRadius: '6px',
                  background: i === active ? wcColors.accent : wcColors.border,
                  display: 'block',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
